import { useEffect, useState, type JSX } from 'react'
import { api, ApiError, type DateToCheck } from '../lib/api'
import { ErrorBox, Loading } from '../components/DataTable'
import { Pager, Status } from '../components/kit'
import { datesLine, kindWord, pageAfterEmpty, pageQuery, pagingHash, readPaging } from '../lib/datesToCheck'
import '../styles/lists.css'

// "Dates to check" (release 3.3, M4), #/report/dates-to-check: for the TPRM
// lead, the vendor records and documents whose stored dates break a rule a
// save now holds (D1, D6: these must be corrected before the stricter check
// is switched on in a later release) or carry a warning worth a look (a
// contract that has ended while the vendor is Active, a lapsed certificate
// with no renewal, a contract shorter than 30 days). Read-only: each row
// links to the place to fix it. A screen of the reports screen, gated by
// reports.read like the reports beside it. The decisions are lib/datesToCheck.ts.

export function DatesToCheck(): JSX.Element {
  const [{ page, size }, setPaging] = useState(() => readPaging(window.location.hash))
  const [data, setData] = useState<{ items: DateToCheck[]; total: number; asOf: string } | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let live = true
    const q = pageQuery(page, size)
    api
      .datesToCheck(q.limit, q.offset)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e instanceof ApiError ? e : new ApiError(0, 'UNKNOWN', String(e))))
    return () => {
      live = false
    }
  }, [page, size])

  function go(nextPage: number, nextSize = size) {
    window.history.replaceState(window.history.state, '', pagingHash(nextPage, nextSize))
    setPaging({ page: nextPage, size: nextSize })
  }

  // An empty page past the first goes back to the first.
  useEffect(() => {
    const back = data ? pageAfterEmpty(page, data.items.length) : null
    if (back !== null) go(back)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page])

  return (
    <>
      <div className="page-head">
        <div>
          <p className="muted">
            <a href="#/reports">Reports</a>
          </p>
          <h1>Dates to check</h1>
          <p className="muted">
            Dates already on file that break a date rule, or are worth a look. Nothing here changes by itself: open the
            record to correct it.
          </p>
        </div>
      </div>

      {error != null && <ErrorBox error={error} what="Dates to check" />}
      {data === null && error == null && <Loading what="the dates to check" />}
      {data !== null && data.items.length === 0 && page === 1 && (
        <p className="note">No stored date breaks a rule or needs a look.</p>
      )}
      {data !== null && data.items.length > 0 && (
        <div className="table-wrap dates-to-check">
          <table className="grid" data-testid="dates-to-check">
            <caption className="vh">Dates to check, as of {data.asOf}</caption>
            <thead>
              <tr>
                <th scope="col">Check</th>
                <th scope="col">Vendor</th>
                <th scope="col">Where</th>
                <th scope="col">Dates</th>
                <th scope="col">What the rule says</th>
                <th scope="col">Fix</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.rule + r.vendorId + (r.documentId ?? '')}>
                  <td>
                    <Status tone={r.kind === 'error' ? 'crit' : 'warn'}>{kindWord(r.kind)}</Status>
                  </td>
                  <td>{r.vendorName}</td>
                  <td>{r.subject}</td>
                  <td className="kit-mono nowrap">{datesLine(r.from, r.to)}</td>
                  <td>{r.message}</td>
                  <td className="nowrap">
                    <a href={r.fix}>{r.documentId ? 'Open documents' : 'Open record'}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data !== null && data.total > 0 && (
        <Pager label="Dates to check pages" page={page} size={size} total={data.total} onPage={(p) => go(p)} onSize={(s) => go(1, s)} />
      )}
    </>
  )
}
